import { useMemo, useState, type CSSProperties, type JSX, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown, type LucideIcon } from 'lucide-react';
import { FetchState } from './FetchState';
import type { Failure } from '../../lib/api-state';

/**
 * Column-driven table primitive. Replaces the dozen-or-so bespoke
 * <table> blocks scattered across admin pages so they share the
 * same row hover, sort affordance, sticky header, and empty/loading
 * handoff to FetchState.
 *
 * Usage:
 *   <DataTable
 *     rows={customers}
 *     rowKey={(r) => r.id}
 *     onRowClick={openDrawer}
 *     loading={loading} error={err} onRetry={refetch}
 *     emptyTitle="No customers yet"
 *     toolbar={<Filters />}
 *     columns={[
 *       { key: 'name',  header: 'Name',  cell: r => r.name, sortable: true },
 *       { key: 'plan',  header: 'Plan',  cell: r => <StatusChip ... /> },
 *       { key: 'mrr',   header: 'MRR',   cell: r => fmt(r.mrr), align: 'right' },
 *     ]}
 *     sort={sort} onSortChange={setSort}
 *   />
 */
export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T, index: number) => ReactNode;
  /**
   * Header becomes a sort control only when something can actually sort:
   * either `sortValue` (the table sorts the rows it was given) or the
   * caller handles `onSortChange` itself. Otherwise no sort affordance is
   * shown, so the header never promises behaviour it does not have.
   */
  sortable?: boolean;
  /** Comparable value for this column; null/undefined always sort last. */
  sortValue?: (row: T) => string | number | null | undefined;
  align?: 'left' | 'right' | 'center';
  width?: string | number;
  /** Hide the column on narrow screens (purely presentational hint, no media query yet). */
  hideOnMobile?: boolean;
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  loading,
  error,
  onRetry,
  emptyTitle = 'No rows',
  emptyDescription,
  emptyAction,
  emptyIcon,
  toolbar,
  sort,
  onSortChange,
  stickyHeader = true,
  density = 'comfortable',
  className,
  caption,
}: {
  rows: T[] | null | undefined;
  columns: Column<T>[];
  rowKey: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  error?: string | Failure | null;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  emptyIcon?: LucideIcon;
  toolbar?: ReactNode;
  sort?: SortState | null;
  onSortChange?: (next: SortState) => void;
  stickyHeader?: boolean;
  density?: 'comfortable' | 'compact';
  className?: string;
  caption?: ReactNode;
}): JSX.Element {
  const [internalSort, setInternalSort] = useState<SortState | null>(null);
  const controlled = sort !== undefined || !!onSortChange;
  const activeSort = controlled ? (sort ?? null) : internalSort;
  const canSort = (col: Column<T>): boolean =>
    !!col.sortable && (!!onSortChange || (!controlled && !!col.sortValue));

  const sortedRows = useMemo(() => {
    if (!rows) return rows;
    if (controlled || !activeSort) return rows;
    const col = columns.find((c) => c.key === activeSort.key);
    const get = col?.sortValue;
    if (!get) return rows;
    const factor = activeSort.dir === 'asc' ? 1 : -1;
    return rows
      .map((row, i) => ({ row, i, v: get(row) }))
      .sort((a, b) => {
        const an = a.v === null || a.v === undefined || a.v === '';
        const bn = b.v === null || b.v === undefined || b.v === '';
        if (an && bn) return a.i - b.i;
        if (an) return 1;   // missing values last in both directions
        if (bn) return -1;
        const cmp = typeof a.v === 'number' && typeof b.v === 'number'
          ? a.v - b.v
          : String(a.v).localeCompare(String(b.v), undefined, { numeric: true, sensitivity: 'base' });
        return cmp !== 0 ? cmp * factor : a.i - b.i;
      })
      .map((x) => x.row);
  }, [rows, columns, activeSort, controlled]);

  const empty = !loading && !error && (!rows || rows.length === 0);
  const padY = density === 'compact' ? 8 : 12;
  const padX = 14;

  const handleHeaderClick = (col: Column<T>): void => {
    if (!canSort(col)) return;
    const nextDir: 'asc' | 'desc' =
      activeSort?.key === col.key && activeSort.dir === 'asc' ? 'desc' : 'asc';
    const next = { key: col.key, dir: nextDir };
    if (onSortChange) onSortChange(next);
    else setInternalSort(next);
  };

  return (
    <div className={className}>
      {(toolbar || caption) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '8px 4px 12px',
            flexWrap: 'wrap',
          }}
        >
          {caption && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{caption}</div>
          )}
          {toolbar && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{toolbar}</div>}
        </div>
      )}

      <FetchState
        loading={loading}
        error={error}
        empty={empty}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        emptyAction={emptyAction}
        emptyIcon={emptyIcon}
        skeleton="rows"
        onRetry={onRetry}
      >
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 14,
            background: 'var(--bg-raised)',
            overflow: 'hidden',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0,
                fontSize: '0.8125rem',
              }}
            >
              <thead>
                <tr>
                  {columns.map((col) => {
                    const sortable = canSort(col);
                    const isSorted = sortable && activeSort?.key === col.key;
                    const headStyle: CSSProperties = {
                      position: stickyHeader ? 'sticky' : 'static',
                      top: 0,
                      zIndex: 1,
                      textAlign: col.align ?? 'left',
                      padding: `${padY}px ${padX}px`,
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'var(--text-muted)',
                      background: 'var(--bg-overlay)',
                      borderBottom: '1px solid var(--border)',
                      cursor: 'default',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      width: col.width,
                    };
                    return (
                      <th
                        key={col.key}
                        scope="col"
                        style={headStyle}
                        aria-sort={
                          !sortable ? undefined
                          : isSorted && activeSort ? (activeSort.dir === 'asc' ? 'ascending' : 'descending')
                          : 'none'
                        }
                      >
                        {(() => {
                          const inner = (
                            <>
                              {col.header}
                              {sortable && (
                                <SortIcon active={isSorted} dir={isSorted && activeSort ? activeSort.dir : undefined} />
                              )}
                            </>
                          );
                          const layout: CSSProperties = {
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            justifyContent:
                              col.align === 'right'
                                ? 'flex-end'
                                : col.align === 'center'
                                ? 'center'
                                : 'flex-start',
                            width: '100%',
                          };
                          return sortable ? (
                            <button
                              type="button"
                              className="data-table__sort"
                              onClick={() => handleHeaderClick(col)}
                              style={{
                                ...layout, background: 'transparent', border: 'none',
                                padding: 0, font: 'inherit', color: 'inherit',
                                letterSpacing: 'inherit', textTransform: 'inherit',
                                cursor: 'pointer',
                              }}
                            >
                              {inner}
                            </button>
                          ) : (
                            <span style={layout}>{inner}</span>
                          );
                        })()}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {(sortedRows ?? []).map((row, i) => {
                  const clickable = !!onRowClick;
                  const onKeyDown = clickable
                    ? (e: KeyboardEvent<HTMLTableRowElement>): void => {
                        // Only react to keys pressed on the row itself, not
                        // on a button/link/input inside it.
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick!(row);
                        }
                      }
                    : undefined;
                  return (
                    <tr
                      key={rowKey(row, i)}
                      onClick={clickable ? () => onRowClick!(row) : undefined}
                      onKeyDown={onKeyDown}
                      tabIndex={clickable ? 0 : undefined}
                      className={clickable ? 'data-table__row--clickable' : undefined}
                      style={{
                        cursor: clickable ? 'pointer' : 'default',
                        transition: 'background 120ms ease',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.background =
                          'rgba(148,163,184,0.06)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.background = 'transparent';
                      }}
                    >
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          style={{
                            padding: `${padY}px ${padX}px`,
                            borderBottom: '1px solid var(--border)',
                            color: 'var(--text-primary)',
                            textAlign: col.align ?? 'left',
                            verticalAlign: 'middle',
                            width: col.width,
                          }}
                        >
                          {col.cell(row, i)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </FetchState>
    </div>
  );
}

function SortIcon({
  active,
  dir,
}: {
  active: boolean;
  dir?: 'asc' | 'desc';
}): JSX.Element {
  if (!active) {
    return <ChevronsUpDown width={11} height={11} style={{ opacity: 0.5 }} />;
  }
  return dir === 'asc' ? (
    <ChevronUp width={11} height={11} />
  ) : (
    <ChevronDown width={11} height={11} />
  );
}

/**
 * Lightweight cell helper for "primary text + muted subtitle" cells —
 * the most common pattern across customer / license / ticket tables.
 *
 * Usage: <td>{primaryWithSub('Acme Corp', 'acme.com')}</td>
 */
export function PrimaryWithSub({
  primary,
  sub,
}: {
  primary: ReactNode;
  sub?: ReactNode;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span
        style={{
          fontWeight: 500,
          color: 'var(--text-primary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {primary}
      </span>
      {sub && (
        <span
          style={{
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {sub}
        </span>
      )}
    </div>
  );
}
